import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import type {Table} from 'dexie';
import {eviteDB, StoredData} from '~/common/indexed-db/dexieStores';
import {UPDATE_CACHED_MEDIA_LIST} from '~/common/reducers/cachedMedia';

type SortOrder = 'asc' | 'desc';

interface UseCachedMediaReturn {
  getCachedMediaDataById: (assetKey: string) => string;
  loading: boolean;
  cachedMediaList: StoredData[];
  removeAndUpdateCachedMediaById: (assetKey: string) => Promise<void>;
  replaceAndUpdateCachedMediaById: (result: string, assetKey: string) => Promise<StoredData | null>;
  removeCachedMediaById: (assetKey: string) => Promise<void>;
  storeAndUpdateCachedMediaById: (result: string, assetKey: string) => Promise<StoredData | null>;
}

const MAX_ENTRIES = 20;
const EMPTY_MEDIA_ENTRIES: {id: string; media: string}[] = [];

export const useCachedMedia = (
  storeName: string,
  sortOrder: SortOrder = 'desc'
): UseCachedMediaReturn => {
  const [loading, setLoading] = useState(true);
  const isAsc = sortOrder === 'asc';
  const dispatch = useDispatch();

  const table: Table<StoredData, string> = useMemo(
    () => eviteDB.table<StoredData, string>(storeName),
    [storeName]
  );

  const mediaEntries = useSelector(
    (state: {cachedMedia: Record<string, {id: string; media: string}[]>}) =>
      state.cachedMedia?.[storeName] ?? EMPTY_MEDIA_ENTRIES
  );

  // Pick the current media by key or newest/oldest
  const getCachedMediaDataById = useCallback(
    (assetKey: string) => {
      if (assetKey) {
        const found = mediaEntries.find((e) => e.id === assetKey);
        if (found) return found.media;
      }
      if (!mediaEntries.length) return '';
      return isAsc ? mediaEntries[0].media : mediaEntries[mediaEntries.length - 1].media;
    },
    [mediaEntries, isAsc]
  );

  const getQuery = useCallback(() => {
    const query = table.orderBy('insertedAt');
    return isAsc ? query : query.reverse();
  }, [table, isAsc]);

  const getCachedMedia = useCallback(async () => {
    setLoading(true);
    try {
      const records = await getQuery().toArray();
      const entries = records.map((r) => ({
        id: r.id,
        media: r.data,
      }));
      dispatch({
        type: UPDATE_CACHED_MEDIA_LIST,
        payload: {storeName, entries},
      });
    } catch (error) {
      evite.error('Error retrieving cached media:', error);
    } finally {
      setLoading(false);
    }
  }, [getQuery, storeName, dispatch]);

  // Adds or updates media in IndexedDB and dispatches update
  const storeAndUpdateCachedMediaById = useCallback(
    async (result: string, assetKey: string) => {
      setLoading(true);
      let newMedia: StoredData | null = null;
      try {
        const count = await table.count();
        if (count >= MAX_ENTRIES) {
          const oldest = await table.orderBy('insertedAt').first();
          if (oldest) await table.delete(oldest.id);
        }
        newMedia = {id: assetKey!, data: result, insertedAt: Date.now()};
        await table.put(newMedia);
        await getCachedMedia();
      } catch (error) {
        evite.error('Error storing media:', error);
      } finally {
        setLoading(false);
      }
      return newMedia;
    },
    [getCachedMedia, table]
  );

  const replaceAndUpdateCachedMediaById = useCallback(
    async (result: string, assetKey: string) => {
      setLoading(true);
      let newMedia: StoredData | null = null;
      try {
        newMedia = {id: assetKey!, data: result, insertedAt: Date.now()};
        await table.put(newMedia);
        await getCachedMedia();
      } catch (error) {
        evite.error('Error replacing media:', error);
      } finally {
        setLoading(false);
      }
      return newMedia;
    },
    [getCachedMedia, table]
  );

  const removeCachedMediaById = useCallback(
    async (assetKey: string) => {
      if (assetKey) await table.delete(assetKey);
    },
    [table]
  );

  const removeAndUpdateCachedMediaById = useCallback(
    async (assetKey: string) => {
      setLoading(true);
      await removeCachedMediaById(assetKey);
      // re-load full list (will dispatch an empty list if nothing left)
      await getCachedMedia();
      setLoading(false);
    },
    [removeCachedMediaById, getCachedMedia]
  );

  const cachedMediaList: StoredData[] = useMemo(
    () =>
      mediaEntries.map((entry) => ({
        id: entry.id,
        data: entry.media,
      })),
    [mediaEntries]
  );

  useEffect(() => {
    getCachedMedia();
  }, [getCachedMedia]);

  return {
    getCachedMediaDataById,
    loading,
    cachedMediaList,
    removeAndUpdateCachedMediaById,
    removeCachedMediaById,
    replaceAndUpdateCachedMediaById,
    storeAndUpdateCachedMediaById,
  };
};
